import { Locale, LocaleContext, Locales, useAuth } from "@amzn/xb-hooks";
import { PropsWithChildren, useState } from "react";
import { Helmet } from "react-helmet";
import { useConfig } from "../../hooks/useConfig";
import { usePostAuthLocale } from "./usePostAuthLocale";
import { usePreAuthLocale } from "./usePreAuthLocale";

export interface LocaleProviderConfig {
  supportedLocales: Locale[];
}

export const LocaleProvider = ({ children, localeOverride }: PropsWithChildren<{ localeOverride?: Locale }>) => {
  const { supportedLocales } = useConfig();
  const { isLoading: isAuthLoading } = useAuth();
  const postAuthLocale = usePostAuthLocale(); // internal hooks only
  const preAuthLocale = usePreAuthLocale(); // internal hooks only

  /* Determine the resolved locale based on the following conditions:
  1. If authentication is loading, set the locale to 'NONE' to avoid flickering in UI.
  2. If the user is authenticated, use the post-authentication locale
  3. If the user is not authenticated, use the pre-authentication locale
  4. If none of the above conditions are met, use the default locale - English.
  */
  const inferredLocale = (isAuthLoading ? Locales.NONE : (postAuthLocale ?? preAuthLocale)) ?? Locales.ENGLISH;

  const localeLookup = Locales.lookupFor(supportedLocales);
  const [selectedLocale, setSelectedLocale] = useState<Locale>();

  const setLocaleAfterLookup = (locale: Locale) => setSelectedLocale(localeLookup(locale.code) ?? Locales.ENGLISH);
  const locale = localeOverride ?? selectedLocale ?? inferredLocale;
  return (
    <LocaleContext.Provider
      value={{
        locale,
        setLocale: setLocaleAfterLookup,
        isLoading: isAuthLoading && !localeOverride && !selectedLocale,
        supportedLocales: supportedLocales,
      }}
    >
      <Helmet>
        {/**
         * s_code uses the "lang" attribute to populate lang for adobe analytics
         * https://tiny.amazon.com/fdjg8fpk/s_codelang
         */}
        <html lang={locale.language} dir={locale.languageDirection ?? "ltr"} />
      </Helmet>
      <>{children}</>
    </LocaleContext.Provider>
  );
};
